import { CdkAccordionModule } from '@angular/cdk/accordion';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, inject, ElementRef, input, Component, ChangeDetectionStrategy, computed, model, contentChild, booleanAttribute, NgModule } from '@angular/core';
import { UxaIconComponent, provideIcons, UxaIconModule } from '@ux-aspects-universal/angular/icon';
import { UxaFocusIndicator } from '@ux-aspects-universal/angular/a11y';
import { chevronDown } from '@ux-aspects-universal/angular/icon/micro-focus';
import { UniqueSelectionDispatcher } from '@angular/cdk/collections';

/**
 * Injection token that can be used to reference instances of `UxaAccordionComponent`. It serves
 * as alternative token to the actual `UxaAccordionComponent` class which could cause circular dependencies.
 */
const _c0 = ["*"];
const _c1 = [[["uxa-accordion-panel-header"]], "*"];
const _c2 = ["uxa-accordion-panel-header", "*"];
const UxaAccordionPanelToken = new InjectionToken('UxaAccordionPanelComponent');
function injectUxaAccordionPanel() {
  return inject(UxaAccordionPanelToken);
}

/**
 * Injection token that can be used to reference instances of `UxaAccordionPanelHeaderComponent`. It serves
 * as alternative token to the actual `UxaAccordionPanelHeaderComponent` class which could cause circular dependencies.
 */
const UxaAccordionPanelHeaderToken = new InjectionToken('UxaAccordionPanelHeaderComponent');
let uniqueId$2 = 1;
class UxaAccordionPanelHeaderComponent {
  constructor() {
    this._elementRef = inject(ElementRef);
    this._focusIndicator = inject(UxaFocusIndicator);
    this.panel = injectUxaAccordionPanel();
    /** @internal Define the unique id */
    this.id = input(`${this.panel._accordionId()}-header-${uniqueId$2++}`);
  }
  ngOnInit() {
    this._focusIndicator.monitor(this._elementRef.nativeElement);
  }
  ngOnDestroy() {
    this._focusIndicator.stopMonitoring(this._elementRef.nativeElement);
  }
  /** Toggle the expanded state of the panel */
  toggle() {
    this.panel.toggle();
  }
  static {
    this.ɵfac = function UxaAccordionPanelHeaderComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaAccordionPanelHeaderComponent)();
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: UxaAccordionPanelHeaderComponent,
      selectors: [["uxa-accordion-panel-header"]],
      hostAttrs: ["role", "button", "tabIndex", "0"],
      hostVars: 5,
      hostBindings: function UxaAccordionPanelHeaderComponent_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵlistener("click", function UxaAccordionPanelHeaderComponent_click_HostBindingHandler() {
            return ctx.toggle();
          })("keydown.space", function UxaAccordionPanelHeaderComponent_keydown_space_HostBindingHandler($event) {
            ctx.toggle();
            return $event.preventDefault();
          })("keydown.enter", function UxaAccordionPanelHeaderComponent_keydown_enter_HostBindingHandler() {
            return ctx.toggle();
          });
        }
        if (rf & 2) {
          i0.ɵɵhostProperty("id", ctx.id());
          i0.ɵɵattribute("aria-controls", ctx.panel.contentId())("aria-expanded", ctx.panel.expanded());
          i0.ɵɵclassProp("uxa-expanded", ctx.panel.expanded());
        }
      },
      inputs: {
        id: [1, "id"]
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: UxaAccordionPanelHeaderToken,
        useExisting: UxaAccordionPanelHeaderComponent
      }], [provideIcons({
        chevronDown
      })])],
      ngContentSelectors: _c0,
      decls: 4,
      vars: 0,
      consts: [[1, "uxa-accordion-header-content"], [1, "uxa-accordion-icon"], ["name", "chevronDown"]],
      template: function UxaAccordionPanelHeaderComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵelementStart(0, "span", 0);
          i0.ɵɵprojection(1);
          i0.ɵɵelementEnd();
          i0.ɵɵelementStart(2, "div", 1);
          i0.ɵɵelement(3, "uxa-icon", 2);
          i0.ɵɵelementEnd();
        }
      },
      dependencies: [UxaIconComponent],
      styles: [".uxa-accordion-header[_ngcontent-%COMP%], [_nghost-%COMP%]{display:flex;align-items:center;flex-direction:var(--uxa-accordion-header__flex-direction, row);gap:var(--uxa-accordion-header__gap);font-family:var(--uxa-accordion-header__font-family, var(--uxa-typography__font-family));font-weight:var(--uxa-accordion-header__font-weight);font-size:var(--uxa-accordion-header__font-size);color:var(--uxa-accordion-header__color);background-color:var(--uxa-accordion-header__background-color);border-top:var(--uxa-accordion-header__border-top-width) var(--uxa-accordion-header__border-top-style) var(--uxa-accordion-header__border-top-color);padding-inline:var(--uxa-accordion-header__padding-inline);padding-block:var(--uxa-accordion-header__padding-block);cursor:pointer}.uxa-accordion-header[_ngcontent-%COMP%]:focus, [_nghost-%COMP%]:focus{outline:none}.uxa-accordion-header.uxa-focused[_ngcontent-%COMP%], .uxa-focused[_nghost-%COMP%]{z-index:1;outline-offset:var(--uxa-accordion-header__focus-outline--offset);outline:var(--uxa-focus-outline__color) var(--uxa-focus-outline__style) var(--uxa-focus-outline__width)}.uxa-accordion-header[_ngcontent-%COMP%]:hover, [_nghost-%COMP%]:hover{--uxa-accordion-header__background-color: var(--uxa-accordion-header__background-color--hover)}.uxa-accordion-header.uxa-expanded[_ngcontent-%COMP%], .uxa-expanded[_nghost-%COMP%]{--uxa-accordion-icon__transform: var(--uxa-accordion-icon__transform--expanded)}.uxa-accordion-header-content[_ngcontent-%COMP%]{flex:1}.uxa-accordion-icon[_ngcontent-%COMP%]{flex:none;height:var(--uxa-accordion-icon__height);transform:var(--uxa-accordion-icon__transform);--uxa-icon__size: var(--uxa-accordion-icon__size);--uxa-icon__fill: var(--uxa-accordion-icon__fill)}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaAccordionPanelHeaderComponent, [{
    type: Component,
    args: [{
      selector: 'uxa-accordion-panel-header',
      changeDetection: ChangeDetectionStrategy.OnPush,
      viewProviders: [provideIcons({
        chevronDown
      })],
      providers: [{
        provide: UxaAccordionPanelHeaderToken,
        useExisting: UxaAccordionPanelHeaderComponent
      }],
      host: {
        role: 'button',
        tabIndex: '0',
        '[id]': 'id()',
        '[attr.aria-controls]': 'panel.contentId()',
        '[attr.aria-expanded]': 'panel.expanded()',
        '[class.uxa-expanded]': 'panel.expanded()',
        '(click)': 'toggle()',
        '(keydown.space)': 'toggle(); $event.preventDefault()',
        '(keydown.enter)': 'toggle()'
      },
      standalone: true,
      imports: [UxaIconComponent],
      template: "<span class=\"uxa-accordion-header-content\">\n  <ng-content />\n</span>\n\n<div class=\"uxa-accordion-icon\">\n  <uxa-icon name=\"chevronDown\" />\n</div>\n",
      styles: [".uxa-accordion-header,:host{display:flex;align-items:center;flex-direction:var(--uxa-accordion-header__flex-direction, row);gap:var(--uxa-accordion-header__gap);font-family:var(--uxa-accordion-header__font-family, var(--uxa-typography__font-family));font-weight:var(--uxa-accordion-header__font-weight);font-size:var(--uxa-accordion-header__font-size);color:var(--uxa-accordion-header__color);background-color:var(--uxa-accordion-header__background-color);border-top:var(--uxa-accordion-header__border-top-width) var(--uxa-accordion-header__border-top-style) var(--uxa-accordion-header__border-top-color);padding-inline:var(--uxa-accordion-header__padding-inline);padding-block:var(--uxa-accordion-header__padding-block);cursor:pointer}.uxa-accordion-header:focus,:host:focus{outline:none}.uxa-accordion-header.uxa-focused,:host.uxa-focused{z-index:1;outline-offset:var(--uxa-accordion-header__focus-outline--offset);outline:var(--uxa-focus-outline__color) var(--uxa-focus-outline__style) var(--uxa-focus-outline__width)}.uxa-accordion-header:hover,:host:hover{--uxa-accordion-header__background-color: var(--uxa-accordion-header__background-color--hover)}.uxa-accordion-header.uxa-expanded,:host.uxa-expanded{--uxa-accordion-icon__transform: var(--uxa-accordion-icon__transform--expanded)}.uxa-accordion-header-content{flex:1}.uxa-accordion-icon{flex:none;height:var(--uxa-accordion-icon__height);transform:var(--uxa-accordion-icon__transform);--uxa-icon__size: var(--uxa-accordion-icon__size);--uxa-icon__fill: var(--uxa-accordion-icon__fill)}\n"]
    }]
  }], null, null);
})();

/**
 * Injection token that can be used to reference instances of `UxaAccordionComponent`. It serves
 * as alternative token to the actual `UxaAccordionComponent` class which could cause circular dependencies.
 */
const UxaAccordionToken = new InjectionToken('UxaAccordionComponent');
let uniqueId$1 = 1;
class UxaAccordionPanelComponent {
  constructor() {
    this._expansionDispatcher = inject(UniqueSelectionDispatcher);
    this._accordion = inject(UxaAccordionToken, {
      skipSelf: true
    });
    /** @internal Access the unique id for the accordion */
    this._accordionId = this._accordion.id;
    /** @internal Define a unique id for the panel */
    this.contentId = computed(() => `${this._accordion.id()}-panel-content-${uniqueId$1++}`);
    /** Whether the content panel is visible. */
    this.expanded = model(false);
    /** @internal Access the header component */
    this.header = contentChild(UxaAccordionPanelHeaderToken);
  }
  ngOnInit() {
    this._removeUniqueSelectionListener = this._expansionDispatcher.listen((id, accordionId) => {
      if (!this._accordion.multiple() && this._accordion.id() === accordionId && this.contentId() !== id) {
        this.expanded.set(false);
      }
    });
  }
  ngOnChanges(changes) {
    if ('expanded' in changes) {
      if (this.expanded()) {
        this._expansionDispatcher.notify(this.contentId(), this._accordion.id());
      }
    }
  }
  ngOnDestroy() {
    this._removeUniqueSelectionListener?.();
  }
  /** @internal Toggle the expanded state. */
  toggle() {
    this.expanded.update(expanded => !expanded);
    this._expansionDispatcher.notify(this.contentId(), this._accordion.id());
  }
  static {
    this.ɵfac = function UxaAccordionPanelComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaAccordionPanelComponent)();
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: UxaAccordionPanelComponent,
      selectors: [["uxa-accordion-panel"]],
      contentQueries: function UxaAccordionPanelComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuerySignal(dirIndex, ctx.header, UxaAccordionPanelHeaderToken, 5);
        }
        if (rf & 2) {
          i0.ɵɵqueryAdvance();
        }
      },
      hostVars: 2,
      hostBindings: function UxaAccordionPanelComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵclassProp("uxa-expanded", ctx.expanded());
        }
      },
      inputs: {
        expanded: [1, "expanded"]
      },
      outputs: {
        expanded: "expandedChange"
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: UxaAccordionToken,
        useValue: undefined
      }, {
        provide: UxaAccordionPanelToken,
        useExisting: UxaAccordionPanelComponent
      }]), i0.ɵɵNgOnChangesFeature],
      ngContentSelectors: _c2,
      decls: 3,
      vars: 2,
      consts: [["role", "region", 1, "uxa-accordion-content", 3, "id"]],
      template: function UxaAccordionPanelComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef(_c1);
          i0.ɵɵprojection(0);
          i0.ɵɵelementStart(1, "div", 0);
          i0.ɵɵprojection(2, 1);
          i0.ɵɵelementEnd();
        }
        if (rf & 2) {
          let tmp_1_0;
          i0.ɵɵadvance();
          i0.ɵɵproperty("id", ctx.contentId());
          i0.ɵɵattribute("aria-labelledby", (tmp_1_0 = ctx.header()) == null ? null : tmp_1_0.id());
        }
      },
      styles: [".uxa-accordion-panel[_ngcontent-%COMP%]   .uxa-accordion-content[_ngcontent-%COMP%], [_nghost-%COMP%]   .uxa-accordion-content[_ngcontent-%COMP%]{display:none}.uxa-accordion-panel.uxa-expanded[_ngcontent-%COMP%] > .uxa-accordion-content[_ngcontent-%COMP%], .uxa-expanded[_nghost-%COMP%] > .uxa-accordion-content[_ngcontent-%COMP%]{display:block}.uxa-accordion-content[_ngcontent-%COMP%]{font-family:var(--uxa-typography-body__font-family, var(--uxa-typography__font-family));font-weight:var(--uxa-typography-body__font-weight);font-size:var(--uxa-typography-body__font-size);text-transform:var(--uxa-typography-body__text-transform);color:var(--uxa-typography-body__color);line-height:var(--uxa-typography-body__line-height);padding-block:var(--uxa-accordion-content__padding-block);padding-inline:var(--uxa-accordion-content__padding-inline)}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaAccordionPanelComponent, [{
    type: Component,
    args: [{
      selector: 'uxa-accordion-panel',
      changeDetection: ChangeDetectionStrategy.OnPush,
      providers: [{
        provide: UxaAccordionToken,
        useValue: undefined
      }, {
        provide: UxaAccordionPanelToken,
        useExisting: UxaAccordionPanelComponent
      }],
      host: {
        '[class.uxa-expanded]': 'expanded()'
      },
      standalone: true,
      template: "<ng-content select=\"uxa-accordion-panel-header\" />\n\n<div\n  class=\"uxa-accordion-content\"\n  role=\"region\"\n  [id]=\"contentId()\"\n  [attr.aria-labelledby]=\"header()?.id()\"\n>\n  <ng-content />\n</div>\n",
      styles: [".uxa-accordion-panel .uxa-accordion-content,:host .uxa-accordion-content{display:none}.uxa-accordion-panel.uxa-expanded>.uxa-accordion-content,:host.uxa-expanded>.uxa-accordion-content{display:block}.uxa-accordion-content{font-family:var(--uxa-typography-body__font-family, var(--uxa-typography__font-family));font-weight:var(--uxa-typography-body__font-weight);font-size:var(--uxa-typography-body__font-size);text-transform:var(--uxa-typography-body__text-transform);color:var(--uxa-typography-body__color);line-height:var(--uxa-typography-body__line-height);padding-block:var(--uxa-accordion-content__padding-block);padding-inline:var(--uxa-accordion-content__padding-inline)}\n"]
    }]
  }], null, null);
})();

/** Used to generate unique ID for each accordion. */
let uniqueId = 1;
class UxaAccordionComponent {
  constructor() {
    /** Whether the chevron should appear at the start or the end of the panel header. */
    this.iconAlign = input('end');
    /** Whether multiple accordion panels may be open at the same time. */
    this.multiple = input(false, {
      transform: booleanAttribute
    });
    /** @internal A unique id for the accordion. */
    this.id = input(`uxa-accordion-${uniqueId++}`);
  }
  static {
    this.ɵfac = function UxaAccordionComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaAccordionComponent)();
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: UxaAccordionComponent,
      selectors: [["uxa-accordion"]],
      hostVars: 3,
      hostBindings: function UxaAccordionComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵhostProperty("id", ctx.id());
          i0.ɵɵclassProp("uxa-accordion-leading-icon", ctx.iconAlign() === "start");
        }
      },
      inputs: {
        iconAlign: [1, "iconAlign"],
        multiple: [1, "multiple"],
        id: [1, "id"]
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: UxaAccordionToken,
        useExisting: UxaAccordionComponent
      }])],
      ngContentSelectors: _c0,
      decls: 1,
      vars: 0,
      template: function UxaAccordionComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵprojection(0);
        }
      },
      styles: [".uxa-accordion[_ngcontent-%COMP%], [_nghost-%COMP%]{display:block;border-bottom:var(--uxa-accordion__border-width) var(--uxa-accordion__border-style) var(--uxa-accordion__border-color)}.uxa-accordion.uxa-accordion-leading-icon[_ngcontent-%COMP%], .uxa-accordion-leading-icon[_nghost-%COMP%]{--uxa-accordion-icon__transform: rotate(-90deg);--uxa-accordion-icon__transform--expanded: rotate(0deg);--uxa-accordion-header__flex-direction: row-reverse;--uxa-accordion-content__padding-inline: var( --uxa-accordion-content__padding-inline--leading-icon )}  [dir=rtl] .uxa-accordion,   [dir=rtl] -shadowcsshost-no-combinator, [dir=rtl][_nghost-%COMP%]   .uxa-accordion[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .uxa-accordion[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]{--uxa-accordion-icon__transform--expanded: rotate(180deg)}  [dir=rtl] .uxa-accordion.uxa-accordion-leading-icon,   [dir=rtl] -shadowcsshost-no-combinator.uxa-accordion-leading-icon, [dir=rtl][_nghost-%COMP%]   .uxa-accordion.uxa-accordion-leading-icon[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .uxa-accordion.uxa-accordion-leading-icon[_ngcontent-%COMP%], [dir=rtl]   .uxa-accordion-leading-icon[_nghost-%COMP%]{--uxa-accordion-icon__transform: rotate(90deg);--uxa-accordion-icon__transform--expanded: rotate(0)}[dir=rtl][_ngcontent-%COMP%]   .uxa-accordion[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]{--uxa-accordion-icon__transform--expanded: rotate(180deg)}[dir=rtl][_ngcontent-%COMP%]   .uxa-accordion.uxa-accordion-leading-icon[_ngcontent-%COMP%], [dir=rtl]   .uxa-accordion-leading-icon[_nghost-%COMP%]{--uxa-accordion-icon__transform: rotate(90deg);--uxa-accordion-icon__transform--expanded: rotate(0)}[_ngcontent-%COMP%]:dir(rtl)   .uxa-accordion[_ngcontent-%COMP%], :dir(rtl)   [_nghost-%COMP%]{--uxa-accordion-icon__transform--expanded: rotate(180deg)}[_ngcontent-%COMP%]:dir(rtl)   .uxa-accordion.uxa-accordion-leading-icon[_ngcontent-%COMP%], :dir(rtl)   .uxa-accordion-leading-icon[_nghost-%COMP%]{--uxa-accordion-icon__transform: rotate(90deg);--uxa-accordion-icon__transform--expanded: rotate(0)}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaAccordionComponent, [{
    type: Component,
    args: [{
      selector: 'uxa-accordion',
      changeDetection: ChangeDetectionStrategy.OnPush,
      providers: [{
        provide: UxaAccordionToken,
        useExisting: UxaAccordionComponent
      }],
      host: {
        '[id]': 'id()',
        '[class.uxa-accordion-leading-icon]': 'iconAlign() === "start"'
      },
      standalone: true,
      template: "<ng-content />\n",
      styles: [".uxa-accordion,:host{display:block;border-bottom:var(--uxa-accordion__border-width) var(--uxa-accordion__border-style) var(--uxa-accordion__border-color)}.uxa-accordion.uxa-accordion-leading-icon,:host.uxa-accordion-leading-icon{--uxa-accordion-icon__transform: rotate(-90deg);--uxa-accordion-icon__transform--expanded: rotate(0deg);--uxa-accordion-header__flex-direction: row-reverse;--uxa-accordion-content__padding-inline: var( --uxa-accordion-content__padding-inline--leading-icon )}::ng-deep [dir=rtl] .uxa-accordion,::ng-deep [dir=rtl] :host,:host-context([dir=rtl]) .uxa-accordion,:host-context([dir=rtl]) :host{--uxa-accordion-icon__transform--expanded: rotate(180deg)}::ng-deep [dir=rtl] .uxa-accordion.uxa-accordion-leading-icon,::ng-deep [dir=rtl] :host.uxa-accordion-leading-icon,:host-context([dir=rtl]) .uxa-accordion.uxa-accordion-leading-icon,:host-context([dir=rtl]) :host.uxa-accordion-leading-icon{--uxa-accordion-icon__transform: rotate(90deg);--uxa-accordion-icon__transform--expanded: rotate(0)}[dir=rtl] .uxa-accordion,[dir=rtl] :host{--uxa-accordion-icon__transform--expanded: rotate(180deg)}[dir=rtl] .uxa-accordion.uxa-accordion-leading-icon,[dir=rtl] :host.uxa-accordion-leading-icon{--uxa-accordion-icon__transform: rotate(90deg);--uxa-accordion-icon__transform--expanded: rotate(0)}:dir(rtl) .uxa-accordion,:dir(rtl) :host{--uxa-accordion-icon__transform--expanded: rotate(180deg)}:dir(rtl) .uxa-accordion.uxa-accordion-leading-icon,:dir(rtl) :host.uxa-accordion-leading-icon{--uxa-accordion-icon__transform: rotate(90deg);--uxa-accordion-icon__transform--expanded: rotate(0)}\n"]
    }]
  }], null, null);
})();
class UxaAccordionModule {
  static {
    this.ɵfac = function UxaAccordionModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaAccordionModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: UxaAccordionModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [CommonModule, CdkAccordionModule, UxaIconModule, UxaAccordionPanelHeaderComponent]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaAccordionModule, [{
    type: NgModule,
    args: [{
      imports: [CommonModule, CdkAccordionModule, UxaIconModule, UxaAccordionComponent, UxaAccordionPanelComponent, UxaAccordionPanelHeaderComponent],
      exports: [UxaAccordionComponent, UxaAccordionPanelComponent, UxaAccordionPanelHeaderComponent]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { UxaAccordionComponent, UxaAccordionModule, UxaAccordionPanelComponent, UxaAccordionPanelHeaderComponent };
